import { AxiosResponse } from 'axios';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'libs/Axios';

import { AxiosDefaultErrorEntity } from 'types/api/Common/ErrorTypes';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import {
  RequestCreateRowenaContent,
  RowenaAvailableVariablesResponse,
  RowenaContentDefinitionDetails,
  RowenaContentDefinitionItem,
} from 'types/api/SystemTenant/RowenaAI/RowenaContentTypes';

export const ROWENA_CONTENT_DEFINITION_QUERY_KEY = {
  CONTENT_LIST: 'ROWENA_CONTENT_LIST',
  CONTENT_DETAILS: 'ROWENA_CONTENT_DETAILS',
  ROWENA_CONTENT_PAGE_TYPE: 'ROWENA_CONTENT_PAGE_TYPE',
  ROWENA_CONTENT_PAGE_LIST: 'ROWENA_CONTENT_PAGE_LIST',
  ROWENA_CONTENT_DISPLAY: 'ROWENA_CONTENT_DISPLAY',
  ROWENA_CONTENT_DEFINITION_LIST: 'ROWENA_CONTENT_DEFINITION_LIST',
  ROWENA_AVAILABLE_VARIABLES: 'ROWENA_AVAILABLE_VARIABLES',
};

export function useGetRowenaContentPageTypesDropdown() {
  return useQuery<AxiosResponse<AutoCompleteItem[]>, AxiosDefaultErrorEntity>(
    [ROWENA_CONTENT_DEFINITION_QUERY_KEY.ROWENA_CONTENT_PAGE_TYPE],
    () => axios.get(`/api/v1/rowena-content-definition/dropdown/page-types`).then((res) => res)
  );
}

export function useGetRowenaAvailableVariables(pageType?: string) {
  return useQuery<AxiosResponse<RowenaAvailableVariablesResponse>, AxiosDefaultErrorEntity>(
    [ROWENA_CONTENT_DEFINITION_QUERY_KEY.ROWENA_AVAILABLE_VARIABLES, pageType],
    () => axios.get(`/api/v1/rowena-content-definition/variables/${pageType}`).then((res) => res),
    {
      enabled: !!pageType,
      staleTime: 1000 * 60 * 60,
    }
  );
}

export function useGetRowenaContentPageListDropdown(pageType?: string) {
  return useQuery<AxiosResponse<AutoCompleteItem[]>, AxiosDefaultErrorEntity>(
    [ROWENA_CONTENT_DEFINITION_QUERY_KEY.ROWENA_CONTENT_PAGE_LIST, pageType],
    () => axios.get(`/api/v1/rowena-content-definition/dropdown/page-list/${pageType}`).then((res) => res),
    {
      enabled: !!pageType,
    }
  );
}

export function useCreateRowenaContent() {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse, AxiosDefaultErrorEntity, RequestCreateRowenaContent>(
    (data) => axios.post(`/api/v1/rowena-content-definition/create`, data).then((res) => res),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(ROWENA_CONTENT_DEFINITION_QUERY_KEY.CONTENT_LIST);
      },
      onError: (error, variables, ctx) => {
        queryClient.invalidateQueries(ROWENA_CONTENT_DEFINITION_QUERY_KEY.CONTENT_LIST);
        queryClient.getDefaultOptions().mutations?.onError?.(error, variables, ctx);
      },
    }
  );
}

export function useGetListRowenaContentDefinitionList() {
  return useQuery<AxiosResponse<RowenaContentDefinitionItem[]>, AxiosDefaultErrorEntity>(
    [ROWENA_CONTENT_DEFINITION_QUERY_KEY.ROWENA_CONTENT_DEFINITION_LIST],
    () => axios.get(`/api/v1/rowena-content-definition/list`).then((res) => res)
  );
}

export function useGetRowenaContentDefinitionDetails(contentId: string | null) {
  return useQuery<AxiosResponse<RowenaContentDefinitionDetails>, AxiosDefaultErrorEntity>(
    [ROWENA_CONTENT_DEFINITION_QUERY_KEY.CONTENT_DETAILS, contentId],
    () => axios.get(`/api/v1/rowena-content-definition/details/${contentId}`).then((res) => res),
    {
      enabled: !!contentId,
      cacheTime: 0,
      staleTime: 0,
    }
  );
}

export function useUpdateRowenaContentDefinitionDetails(contentId: string | null) {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse, AxiosDefaultErrorEntity, RequestCreateRowenaContent>(
    (data) => axios.put(`/api/v1/rowena-content-definition/update/${contentId}`, data).then((res) => res),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(ROWENA_CONTENT_DEFINITION_QUERY_KEY.CONTENT_LIST);
        queryClient.invalidateQueries(ROWENA_CONTENT_DEFINITION_QUERY_KEY.CONTENT_DETAILS);
      },
      onError: (error, variables, ctx) => {
        queryClient.invalidateQueries(ROWENA_CONTENT_DEFINITION_QUERY_KEY.CONTENT_LIST);
        queryClient.getDefaultOptions().mutations?.onError?.(error, variables, ctx);
      },
    }
  );
}

export function useDeleteRowenaContentDefinition() {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse, AxiosDefaultErrorEntity, { contentId: string }>(
    (body) => axios.delete(`/api/v1/rowena-content-definition/delete/${body.contentId}`).then((res) => res),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(ROWENA_CONTENT_DEFINITION_QUERY_KEY.CONTENT_LIST);
        queryClient.invalidateQueries(ROWENA_CONTENT_DEFINITION_QUERY_KEY.CONTENT_DETAILS);
      },
      onError: (error, variables, ctx) => {
        queryClient.getDefaultOptions().mutations?.onError?.(error, variables, ctx);
      },
    }
  );
}
